/**
 * Process Repository Hook
 * Fetches external records and builds a tree structure for process hierarchies.
 * Delegates data fetching and field detection to the universal useDomainRepository hook.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useDomainRepository, type RawExternalRecord } from '../../data-binding/hooks/useDomainRepository';
import { ProcessProfile } from '../ProcessProfile';
import { useAuthenticatedFetch } from '../../../hooks/useAuth';

/**
 * A process record from the external repository
 */
export interface ProcessRecord {
  id: string;
  name: string;
  parentId: string | null;
  status?: string;
  owner?: string;
  description?: string;
  data: Record<string, unknown>;
  /**
   * When set, this row is a READ-ONLY federated record from another process
   * folder (unification P5) — the value is the source folder's name for the
   * provenance badge. Own records leave this undefined.
   */
  federatedFrom?: string | null;
}

/**
 * Tree node representing a process with its children
 */
export interface ProcessTreeNode {
  record: ProcessRecord;
  children: ProcessTreeNode[];
  depth: number;
  isExpanded?: boolean;
}

/**
 * Configuration for field mapping
 */
export interface ProcessFieldMapping {
  idColumn: string;
  nameColumn: string;
  parentColumn: string | null;
  statusColumn: string | null;
  ownerColumn: string | null;
  descriptionColumn: string | null;
}

/**
 * Convert generic field mapping to ProcessFieldMapping
 */
function toProcessFieldMapping(mapping: Record<string, string | null>): ProcessFieldMapping {
  return {
    idColumn: mapping.processId ?? 'id',
    nameColumn: mapping.name ?? 'name',
    parentColumn: mapping.parentProcessId ?? null,
    statusColumn: mapping.status ?? null,
    ownerColumn: mapping.owner ?? null,
    descriptionColumn: mapping.description ?? null,
  };
}

/**
 * Convert raw record to ProcessRecord using field mappings
 */
function toProcessRecord(
  raw: RawExternalRecord,
  mapping: ProcessFieldMapping
): ProcessRecord {
  const data = raw.data;
  return {
    id: String(data[mapping.idColumn] ?? raw.id),
    name: String(data[mapping.nameColumn] ?? 'Unnamed'),
    parentId: mapping.parentColumn
      ? (data[mapping.parentColumn] as string | null) ?? null
      : null,
    status: mapping.statusColumn
      ? String(data[mapping.statusColumn] ?? '')
      : undefined,
    owner: mapping.ownerColumn
      ? String(data[mapping.ownerColumn] ?? '')
      : undefined,
    description: mapping.descriptionColumn
      ? String(data[mapping.descriptionColumn] ?? '')
      : undefined,
    data,
  };
}

/**
 * Build tree structure from flat records
 */
function buildTree(
  records: ProcessRecord[],
  filterText?: string
): ProcessTreeNode[] {
  const recordMap = new Map<string, ProcessRecord>();
  records.forEach((r) => recordMap.set(r.id, r));

  let filteredRecords = records;
  if (filterText) {
    const searchLower = filterText.toLowerCase();
    filteredRecords = records.filter(
      (r) =>
        r.name.toLowerCase().includes(searchLower) ||
        r.id.toLowerCase().includes(searchLower) ||
        (r.status && r.status.toLowerCase().includes(searchLower))
    );

    const includedIds = new Set(filteredRecords.map((r) => r.id));
    filteredRecords.forEach((r) => {
      let currentId = r.parentId;
      while (currentId && !includedIds.has(currentId)) {
        const parent = recordMap.get(currentId);
        if (parent) {
          filteredRecords.push(parent);
          includedIds.add(currentId);
          currentId = parent.parentId;
        } else {
          break;
        }
      }
    });
  }

  const childrenMap = new Map<string | null, ProcessRecord[]>();
  filteredRecords.forEach((r) => {
    const parentId = r.parentId;
    if (!childrenMap.has(parentId)) {
      childrenMap.set(parentId, []);
    }
    childrenMap.get(parentId)!.push(r);
  });

  function buildNode(record: ProcessRecord, depth: number): ProcessTreeNode {
    const children = childrenMap.get(record.id) ?? [];
    return {
      record,
      children: children
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((child) => buildNode(child, depth + 1)),
      depth,
      isExpanded: depth < 2,
    };
  }

  const roots = childrenMap.get(null) ?? [];
  return roots
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((r) => buildNode(r, 0));
}

/**
 * Flatten tree to array for rendering (respecting expanded state)
 */
export function flattenTree(
  nodes: ProcessTreeNode[],
  expandedIds: Set<string>
): ProcessTreeNode[] {
  const result: ProcessTreeNode[] = [];

  function traverse(node: ProcessTreeNode) {
    result.push(node);
    if (expandedIds.has(node.record.id) && node.children.length > 0) {
      node.children.forEach(traverse);
    }
  }

  nodes.forEach(traverse);
  return result;
}

export interface UseProcessRepositoryOptions {
  sourceId: string | undefined;
  folderId?: string;
  filterText?: string;
  fieldMapping?: Partial<ProcessFieldMapping>;
  limit?: number;
}

export interface UseProcessRepositoryResult {
  tree: ProcessTreeNode[];
  records: ProcessRecord[];
  fieldMapping: ProcessFieldMapping | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
  totalCount: number;
  hasMore: boolean;
}

/**
 * Hook to fetch and structure process repository data.
 * Delegates data fetching to useDomainRepository(ProcessProfile).
 *
 * @example
 * ```tsx
 * const { tree, isLoading, records, fieldMapping } = useProcessRepository({
 *   sourceId: dataSource?.id,
 *   filterText: searchQuery,
 * });
 * ```
 */
export function useProcessRepository(
  options: UseProcessRepositoryOptions
): UseProcessRepositoryResult {
  const {
    sourceId,
    folderId,
    filterText,
    fieldMapping: customMapping,
    limit = 500,
  } = options;
  const authenticatedFetch = useAuthenticatedFetch();

  const {
    rawRecords,
    fieldMapping: genericMapping,
    isLoading,
    error,
    refetch,
    totalCount,
    hasMore,
  } = useDomainRepository({
    sourceId,
    profile: ProcessProfile,
    limit,
  });

  const compassRecordsQuery = useQuery<{
    records: RawExternalRecord[];
    total: number;
    hasMore: boolean;
  }>({
    queryKey: ['compass', 'process-repository', folderId ?? '', filterText ?? '', limit],
    enabled: !sourceId && !!folderId,
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filterText) {
        params.set('search', filterText);
      }
      const response = await authenticatedFetch(
        `/api/compass/folders/${folderId}/process-repository?${params.toString()}`,
      );
      if (!response.ok) {
        throw new Error('Failed to fetch Compass process repository');
      }
      return response.json();
    },
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
  });

  const effectiveRawRecords = useMemo(
    () => (sourceId ? rawRecords : (compassRecordsQuery.data?.records ?? [])),
    [sourceId, rawRecords, compassRecordsQuery.data?.records],
  );

  // Convert generic mapping to ProcessFieldMapping
  const detectedMapping = useMemo((): ProcessFieldMapping | null => {
    if (!sourceId && folderId) {
      return {
        idColumn: 'processId',
        nameColumn: 'name',
        parentColumn: 'parentProcessId',
        statusColumn: 'status',
        ownerColumn: 'owner',
        descriptionColumn: 'description',
      };
    }
    if (Object.keys(genericMapping).length === 0) return null;
    return toProcessFieldMapping(genericMapping);
  }, [sourceId, folderId, genericMapping]);

  // Merge custom mapping with detected
  const effectiveMapping = useMemo((): ProcessFieldMapping | null => {
    if (!detectedMapping) return null;
    if (!customMapping) return detectedMapping;
    return { ...detectedMapping, ...customMapping };
  }, [detectedMapping, customMapping]);

  // Convert raw records to ProcessRecords
  const processRecords = useMemo((): ProcessRecord[] => {
    if (effectiveRawRecords.length === 0 || !effectiveMapping) return [];
    return effectiveRawRecords.map((raw) => toProcessRecord(raw, effectiveMapping));
  }, [effectiveRawRecords, effectiveMapping]);

  // Build tree structure
  const tree = useMemo((): ProcessTreeNode[] => {
    return buildTree(processRecords, filterText);
  }, [processRecords, filterText]);

  return {
    tree,
    records: processRecords,
    fieldMapping: effectiveMapping,
    isLoading: sourceId ? isLoading : compassRecordsQuery.isLoading,
    error: (sourceId ? error : compassRecordsQuery.error) as Error | null,
    refetch: sourceId ? refetch : () => {
      void compassRecordsQuery.refetch();
    },
    totalCount: sourceId ? totalCount : (compassRecordsQuery.data?.total ?? 0),
    hasMore: sourceId ? hasMore : (compassRecordsQuery.data?.hasMore ?? false),
  };
}
